import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { EstadoVacio } from '@/ui/EstadoVacio'
import { LineaEsqueleto } from '@/ui/Esqueleto'
import { useEstadoContabilidad } from '@/dominio/contabilidad/useContabilidad'
import { useCuentasBancarias } from '@/dominio/bancos/useBancos'
import { EncabezadoPantalla } from './-pantalla'
import { ErrorCompras, Secciones } from './-compras/comun'
import { CuentasBancarias } from './-bancos/Cuentas'
import { Cheques, Movimientos } from './-bancos/Movimientos'
import { ImportarEstadoCuenta } from './-bancos/Importar'
import { Conciliar } from './-bancos/Conciliar'

/**
 * Bancos (`docs/adr/0028` §5 y §6), del complemento de contabilidad: las
 * cuentas y cajas chicas, sus movimientos, el estado de cuenta importado, la
 * conciliación y los cheques. La sección va en la URL.
 */

const SECCIONES = [
  { id: 'cuentas', etiqueta: 'Cuentas' },
  { id: 'movimientos', etiqueta: 'Movimientos' },
  { id: 'importar', etiqueta: 'Importar' },
  { id: 'conciliar', etiqueta: 'Conciliar' },
  { id: 'cheques', etiqueta: 'Cheques' },
] as const

type IdSeccion = (typeof SECCIONES)[number]['id']

export const Route = createFileRoute('/bancos')({
  validateSearch: (b: Record<string, unknown>): { seccion?: IdSeccion } => ({
    seccion: SECCIONES.some((s) => s.id === b.seccion) && b.seccion !== 'cuentas' ? (b.seccion as IdSeccion) : undefined,
  }),
  component: PantallaBancos,
})

function PantallaBancos() {
  const estado = useEstadoContabilidad()
  const contratada = estado.data?.contratada === true
  const cuentas = useCuentasBancarias(contratada)
  const navegar = useNavigate({ from: Route.fullPath })
  const seccion = Route.useSearch().seccion ?? 'cuentas'

  if (estado.isLoading) return <LineaEsqueleto ancho="w-1/2" />

  if (!contratada) {
    return (
      <div className="flex max-w-[900px] flex-col gap-4">
        <EncabezadoPantalla titulo="Bancos" />
        <EstadoVacio
          variante="primera-vez"
          mensaje="Bancos va con el complemento de contabilidad: cuentas y cajas chicas, el estado de cuenta importado, la conciliación y los cheques. Para encenderlo, hable con Agentical Group."
        />
      </div>
    )
  }

  const lista = cuentas.data ?? []

  return (
    <div className="flex flex-col gap-4">
      <EncabezadoPantalla
        titulo="Bancos"
        descripcion="Lo que entra y sale de cada cuenta según los libros, casado con lo que dice el banco. Los pagos y los gastos llegan solos; aquí van los demás movimientos."
      />
      <Secciones
        etiqueta="Secciones de bancos"
        secciones={SECCIONES}
        actual={seccion}
        irA={(s) => void navegar({ search: { seccion: s === 'cuentas' ? undefined : s }, replace: true })}
      />
      <ErrorCompras error={cuentas.error} />
      {seccion === 'cuentas' ? <CuentasBancarias /> : null}
      {seccion !== 'cuentas' && !cuentas.data ? <LineaEsqueleto ancho="w-1/2" /> : null}
      {seccion !== 'cuentas' && cuentas.data && lista.length === 0 ? (
        <EstadoVacio variante="primera-vez" mensaje="Abra primero una cuenta en la sección Cuentas." />
      ) : null}
      {lista.length > 0 ? (
        <>
          {seccion === 'movimientos' ? <Movimientos cuentas={lista} /> : null}
          {seccion === 'importar' ? <ImportarEstadoCuenta cuentas={lista} /> : null}
          {seccion === 'conciliar' ? <Conciliar cuentas={lista} /> : null}
          {seccion === 'cheques' ? <Cheques cuentas={lista} /> : null}
        </>
      ) : null}
    </div>
  )
}
